import PropTypes from "prop-types";
import React, { Component } from "react";
import withStyles from "@mui/styles/withStyles";
import Calendar from "../../components/Calendar/Calendar";
import { Box, Paper } from "@mui/material";
import moment from "moment";
import ControlPanel from "../../components/Calendar/ControlPanel";
import CalendarPageLayout from "../../components/FullPageLayout/CalendarPageLayout";
import CalendarQuickActions from "../../components/Calendar/CalendarQuickActions";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import CalendarTooltips from "./CalendarTooltips";

const monthView = "dayGridMonth";

const learningPathAssignmentsColor = "#2E3192";

const styles = theme => ({
  calendarContainer: {
    paddingBottom: theme.spacing(4),
    flex: 1,
    maxWidth: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: `calc(100% - ${theme.sizes.calendarControlPanelWidthCodensedMobile}px)`,
      minHeight: `calc(100vh - ${theme.sizes.calendarHeaderHeight}px)`
    }
  }
});

class CalendarView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      calendarTitle: "",
      currentView: monthView,
      scheduleView: false,
      clickedDate: null,
      calendarGroups: null,
      allCalendarEvents: null,
      filteredCalendarEvents: null,
      hiddenCalendars: []
    };

    this.calendarRef = React.createRef();
  }

  handleViewChange = event => {
    if (event.view.type !== this.state.currentView) {
      this.setState({ currentView: event.view.type });
    }
  };

  /**
   * Uses the calendars object to find the event's color based on the calendar
   * the event belongs to. If calendars is undefined, returns the theme's primary color.
   */
  createEvents = () => {
    const { theme } = this.props;
    const { allCalendars } = this.state;
    const allCalendarEvents = [];
    allCalendars &&
      allCalendars.forEach(calendar => {
        if (calendar.events) {
          const color = calendar.color
            ? calendar.color
            : theme.palette.primary.main;
          allCalendarEvents.push(
            ...calendar.events.map((event, index) => {
              return {
                ...event,
                calendarName: calendar.name,
                backgroundColor: color,
                borderColor: color,
                textColor:
                  color === learningPathAssignmentsColor
                    ? theme.palette.common.white
                    : theme.palette.text.primary
              };
            })
          );
        }
      });
    this.setState({ allCalendarEvents }, () => this.filterEvents());
  };

  /**
   * Filters the events by selected calendars in the control panel
   */

  filterEvents = () => {
    const { allCalendarEvents, hiddenCalendars } = this.state;
    const filteredCalendarEvents =
      allCalendarEvents &&
      allCalendarEvents.filter(
        event => !hiddenCalendars.includes(event.calendarName)
      );
    this.setState({ filteredCalendarEvents });
  };

  /**
   * Gets the column header for the calendar views. For the month, returns
   * the three letter day of the week. For the week and day views, we have to return
   * the html for the column headeras a string due to a limitation of the
   * Full Calendar library
   */
  getColumnHeader = date => {
    const activeDate = moment(date).isSame(moment(), "day");
    const weekday = moment(date).format("ddd");
    if (this.state.currentView === monthView) {
      return weekday;
    } else {
      return `<div class="timeGrid-col-header ${
        activeDate ? "active" : ""
      }"><span class="timeGrid-col-header-day">${weekday}</span>
      <span class="timeGrid-col-header-date">${date.getDate()}</span>
      <span class="timeGrid-col-header-border"/></div>`;
    }
  };

  getEvent = info => {
    if (info) {
      const { t } = this.props;
      const eventTitle =
        info.event.extendedProps.type === "assignment"
          ? t("learningCenterCalendar.assignmentCalendarTitle", {
              course_name: info.event.title
            })
          : info.event.title;
      if (this.state.currentView !== monthView) {
        const eventStartTime = moment(info.event.start);
        const eventTime = eventStartTime.format(
          `h${eventStartTime.get("minutes") > 0 ? ":mma" : "a"}`
        );
        info.el.innerHTML = `<div class="timeGrid-event"><span class="fc-title">${eventTitle}</span>
                            <span class="fc-time">${eventTime}</span>
                          </div>`;
      } else {
        info.el.innerHTML = `<div><span>${eventTitle}</span></div>`;
      }
    }
  };

  handleShowScheduleView = scheduleView => {
    this.setState({ scheduleView });
  };

  handleDateClickedInPanel = clickedDate => {
    this.setState({ clickedDate, scheduleView: false });
  };

  componentDidUpdate() {
    const { calendarLists, allCalendars, hiddenCalendars } = this.props;
    if (
      calendarLists !== this.state.calendarLists ||
      allCalendars !== this.state.allCalendars
    ) {
      this.setState({ calendarLists, allCalendars }, () => {
        this.createEvents();
      });
    }
    if (hiddenCalendars !== this.state.hiddenCalendars) {
      this.setState({ hiddenCalendars }, () => {
        this.filterEvents();
      });
    }
  }

  render() {
    const {
      userPermissions,
      classes,
      onSelectedCalendarsChange,
      calendarLists,
      onCreateNewEvent,
      onUpdateEvent,
      onExportModalOpenChange,
      onImportModalOpenChange,
      onSettingsModalOpenChange,
      onCreateEventModalOpenChange,
      onEditEventModalOpenChange,
      onCreateCalendarModalOpenChange,
      customerName,
      t
    } = this.props;
    const {
      filteredCalendarEvents,
      clickedDate,
      scheduleView,
      allCalendars
    } = this.state;

    const header = {
      left: "prev, next",
      center: "title",
      right: !scheduleView
        ? "timeGridDay,timeGridWeek,dayGridMonth calendarView,scheduleView"
        : "calendarView,scheduleView"
    };

    const buttons = {
      calendarView: {
        text: "Calendar",
        click: () => this.handleShowScheduleView(false)
      },
      scheduleView: {
        text: "Schedule",
        click: () => this.handleShowScheduleView(true)
      }
    };

    return (
      <CalendarPageLayout
        headerActions={
          <CalendarQuickActions
            userPermissions={userPermissions}
            onSettingsModalOpenChange={onSettingsModalOpenChange}
            onExportModalOpenChange={onExportModalOpenChange}
            onImportModalOpenChange={onImportModalOpenChange}
            calendarRef={this.calendarRef}
          />
        }
        title={
          <Box fontWeight="fontWeightRegular">
            {t("learningCenterCalendar.title")}
          </Box>
        }
      >
        <Box display="flex" position="relative">
          <Paper className={classes.calendarContainer}>
            <CalendarTooltips delayHide={100} events={filteredCalendarEvents}>
              {({ onMouseEnter }) => (
                <Calendar
                  scheduleView={scheduleView}
                  columnHeaderHtml={this.getColumnHeader}
                  events={filteredCalendarEvents}
                  header={header}
                  customButtons={buttons}
                  viewRendered={view => this.handleViewChange(view)}
                  fullScreen
                  eventRender={this.getEvent}
                  clickedDate={clickedDate}
                  calendars={allCalendars}
                  handleEventClicked={onEditEventModalOpenChange}
                  ref={this.calendarRef}
                  printTitle={`${customerName} ${t(
                    "learningCenterCalendar.title"
                  )}`}
                  onMouseEnter={onMouseEnter}
                />
              )}
            </CalendarTooltips>
          </Paper>
          <ControlPanel
            userPermissions={userPermissions}
            onImportModalOpenChange={onImportModalOpenChange}
            onExportModalOpenChange={onExportModalOpenChange}
            onCreateEventModalOpenChange={onCreateEventModalOpenChange}
            onEditEventModalOpenChange={onEditEventModalOpenChange}
            events={filteredCalendarEvents}
            calendarLists={calendarLists}
            onSelectedCalendarsChange={onSelectedCalendarsChange}
            handleDateClicked={this.handleDateClickedInPanel}
            onCreateNewEvent={onCreateNewEvent}
            onUpdateEvent={onUpdateEvent}
            onCreateCalendarModalOpenChange={onCreateCalendarModalOpenChange}
          />
        </Box>
      </CalendarPageLayout>
    );
  }
}

const TranslatedCalendarView = withTranslation()(CalendarView);

const mapStateToProps = state => ({
  hiddenCalendars: state.calendarState.hiddenCalendars,
  customerName: state.customerState.customer.name
});

export default connect(mapStateToProps)(
  withStyles(styles, { withTheme: true })(TranslatedCalendarView)
);

CalendarView.propTypes = {
  userPermissions: PropTypes.any,
  calendarLists: PropTypes.any,
  classes: PropTypes.any,
  hiddenCalendars: PropTypes.any,
  onCreateNewEvent: PropTypes.any,
  onUpdateEvent: PropTypes.any,
  onImportNewCalendar: PropTypes.any,
  onSelectedCalendarsChange: PropTypes.any,
  theme: PropTypes.any,
  customerName: PropTypes.string
};
